/**
 * RoleTableHeader Component
 * Displays table header with role names and edit/lock icons
 * Supports drag & drop for reordering roles
 */

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * DraggableRoleHeader - Individual draggable role header
 */
const DraggableRoleHeader = ({ 
  role, 
  isRoleLocked, 
  formatRoleName, 
  onEditRole, 
  hoveredRole, 
  setHoveredRole,
  canReorder
}) => {
  const isLocked = isRoleLocked(role);
  // Super Admin role (id: 1) cannot be reordered, but other locked roles can be reordered if user has permission
  const isSuperAdmin = Number(role.id) === 1;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `role-${role.id}`,
    disabled: isSuperAdmin || !canReorder // Disable dragging only for Super Admin or if user doesn't have reorder permission
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: (isSuperAdmin || !canReorder) ? 'default' : 'grab', // Only Super Admin or users without permission should show default cursor
  };

  return (
    <th 
      ref={setNodeRef}
      style={style}
      className={`role-header ${isLocked ? 'locked' : 'draggable'} ${isDragging ? 'dragging' : ''}`}
      onMouseEnter={() => setHoveredRole(role.id)}
      onMouseLeave={() => setHoveredRole(null)}
    >
      <div className="role-header-content">
        {!isSuperAdmin && canReorder && (
          <i 
            className="bi bi-grip-vertical drag-handle"
            {...attributes}
            {...listeners}
            aria-hidden="true"
          ></i>
        )}
        <span>{formatRoleName(role.name)}</span>
        {isLocked ? (
          <i 
            className="bi bi-lock-fill lock-icon"
          ></i>
        ) : (
          <i 
            className="bi bi-pencil edit-icon" 
            onClick={(e) => {
              e.stopPropagation();
              onEditRole(role);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          ></i>
        )}
      </div>
      
      {/* Tooltip for all roles */}
      {hoveredRole === role.id && (
        <div className="role-tooltip">
          {isLocked 
            ? `${formatRoleName(role.name)} permissions are locked`
            : `Drag handle to reorder or click pencil to edit ${formatRoleName(role.name)} role`
          }
        </div>
      )}
    </th>
  );
};

const RoleTableHeader = ({ 
  roles, 
  isRoleLocked, 
  formatRoleName, 
  onEditRole, 
  hoveredRole, 
  setHoveredRole,
  canReorder
}) => {
  return (
    <thead>
      <tr>
        <th className="permission-header">Permissions</th>
        {roles.map(role => (
          <DraggableRoleHeader
            key={role.id}
            role={role}
            isRoleLocked={isRoleLocked}
            formatRoleName={formatRoleName}
            onEditRole={onEditRole}
            hoveredRole={hoveredRole}
            setHoveredRole={setHoveredRole}
            canReorder={canReorder}
          />
        ))}
      </tr>
    </thead>
  );
};

export default RoleTableHeader;

